import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Loader2, Plus, Save, Trash2, Tag, CheckCircle2,
} from "lucide-react";
import { Header } from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";

const BUILTIN_IDS = new Set(["long", "short", "note"]);

export default function AdminPostTypes() {
  const [types, setTypes] = useState(null);
  const [error, setError] = useState("");
  const [newId, setNewId] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/post-types");
      setTypes(r.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const slug = newId
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  const canCreate =
    slug.length > 0 &&
    newLabel.trim().length > 0 &&
    !(types || []).some((t) => t.id === slug);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    setCreating(true);
    setError("");
    try {
      await api.post("/admin/post-types", { id: slug, label: newLabel.trim() });
      setNewId("");
      setNewLabel("");
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]"
      data-testid="page-admin-post-types"
    >
      <Header />
      <main className="mx-auto max-w-4xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to admin
        </RouterLink>

        <div className="mt-6 mb-8">
          <div className="flex items-center gap-2 text-brand-accent">
            <Tag size={16} strokeWidth={2.5} />
            <span className="label-kicker">Post types</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Manage the tags beside your posts.
          </h1>
          <p className="mt-2 text-sm text-[var(--brand-body)]">
            The three built-in types (<code>long</code>, <code>short</code>, <code>note</code>) stay forever. Add your own — match report, pub review, transfer watch, whatever — and they'll show up as options when you write a new post.
          </p>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="post-types-error"
          >
            {error}
          </p>
        ) : null}

        {/* Add new */}
        <form
          onSubmit={onCreate}
          className="mb-8 rounded-md border border-black/10 bg-[#F8FAFC] p-5"
          data-testid="post-type-create-form"
        >
          <p className="label-kicker text-[var(--brand-body)]">Add a new type</p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <label className="block">
              <span className="label-kicker text-[var(--brand-body)]">Label (visible)</span>
              <input
                type="text"
                value={newLabel}
                onChange={(e) => {
                  setNewLabel(e.target.value);
                  if (!newId) {
                    setNewId(
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9-]+/g, "-")
                        .replace(/^-+|-+$/g, ""),
                    );
                  }
                }}
                maxLength={60}
                placeholder="Match report"
                data-testid="post-type-new-label"
                className="mt-1 w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="label-kicker text-[var(--brand-body)]">Slug (id)</span>
              <input
                type="text"
                value={newId}
                onChange={(e) => setNewId(e.target.value)}
                maxLength={40}
                placeholder="match-report"
                data-testid="post-type-new-id"
                className="mt-1 w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs"
              />
              <span className="mt-1 block text-xs text-[var(--brand-body)]">
                Lowercase + hyphens only: <code>{slug || "—"}</code>
              </span>
            </label>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={!canCreate || creating}
                data-testid="post-type-create-submit"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-semibold text-white hover:bg-brand-accent disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Add type
              </button>
            </div>
          </div>
        </form>

        {/* Existing list */}
        {types === null ? (
          <p className="text-sm text-[var(--brand-body)]">Loading…</p>
        ) : types.length === 0 ? (
          <p className="text-sm text-[var(--brand-body)]">No post types yet.</p>
        ) : (
          <div className="space-y-3" data-testid="post-types-list">
            {types.map((t) => (
              <TypeRow
                key={t.id}
                type={t}
                onSaved={refresh}
                setOuterError={setError}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function TypeRow({ type, onSaved, setOuterError }) {
  const [label, setLabel] = useState(type.label);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLabel(type.label);
  }, [type.label]);

  const dirty = label.trim() !== type.label;
  const isBuiltin = BUILTIN_IDS.has(type.id);

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    setOuterError("");
    try {
      await api.put(`/admin/post-types/${type.id}`, { label: label.trim() });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await onSaved();
    } catch (e) {
      setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (isBuiltin) return;
    if (
      !window.confirm(
        `Delete post type "${type.label}"? Existing posts using it will still show their type tag but it won't be picked from the new-post menu.`,
      )
    )
      return;
    setOuterError("");
    try {
      await api.delete(`/admin/post-types/${type.id}`);
      await onSaved();
    } catch (e) {
      setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-md border border-black/10 bg-white p-4"
      data-testid={`post-type-row-${type.id}`}
    >
      <span
        className="rounded-sm bg-[#6CABDD] px-2 py-1 label-kicker text-white"
        data-testid={`post-type-preview-${type.id}`}
      >
        {label || type.id}
      </span>
      <span className="font-mono text-xs text-[var(--brand-body)]">
        id: <code>{type.id}</code>
      </span>
      {isBuiltin ? (
        <span className="rounded-sm border border-black/10 px-2 py-0.5 text-xs text-[var(--brand-body)]">
          Built-in
        </span>
      ) : null}
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        maxLength={60}
        data-testid={`post-type-label-${type.id}`}
        className="ml-auto w-48 rounded-md border border-black/15 bg-white px-3 py-1.5 text-sm"
      />
      <button
        type="button"
        onClick={save}
        disabled={!dirty || saving}
        data-testid={`post-type-save-${type.id}`}
        className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-3 py-1.5 font-display text-xs font-semibold text-white hover:bg-brand-accent disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
        Save
      </button>
      {saved ? (
        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
          <CheckCircle2 size={12} /> Saved
        </span>
      ) : null}
      {!isBuiltin ? (
        <button
          type="button"
          onClick={remove}
          data-testid={`post-type-delete-${type.id}`}
          className="inline-flex items-center gap-1 rounded-md border border-[#E63946]/40 px-2 py-1.5 text-xs text-[#E63946] hover:bg-[#E63946] hover:text-white"
        >
          <Trash2 size={12} />
        </button>
      ) : null}
    </div>
  );
}
